import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { progressAPI, sentencesAPI } from './api';
import './PersonalCenter.css';

function PersonalCenter({
  user,
  categories,
  completedCounts,
  words,
  onBack,
  userProfile,
  setUserProfile,
  onResetCategory,
}) {
  const [newDisplayName, setNewDisplayName] = useState(userProfile.displayName || '');
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [masteredStats, setMasteredStats] = useState({});
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  const [isReleasing, setIsReleasing] = useState(false);
  const [releasingWord, setReleasingWord] = useState(null);
  const [isResetting, setIsResetting] = useState(false);
  const [resettingCategory, setResettingCategory] = useState(null);
  const [sentenceStats, setSentenceStats] = useState(null);
  const [isLoadingSentenceStats, setIsLoadingSentenceStats] = useState(false);

  // 账号管理状态
  const [accountTab, setAccountTab] = useState('wx'); // 'wx' | 'password' | 'email' | 'delete'
  const [wxBindStatus, setWxBindStatus] = useState(null); // null | 'bound' | 'unbound'
  const [accountMsg, setAccountMsg] = useState({ text: '', type: '' });
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [bindEmail, setBindEmail] = useState('');
  const [bindEmailPassword, setBindEmailPassword] = useState('');
  const [isAccountLoading, setIsAccountLoading] = useState(false);
  const [showAccountModal, setShowAccountModal] = useState(false);
  
  // 排行榜相关状态
  const [leaderboard, setLeaderboard] = useState([]);
  const [currentUserRank, setCurrentUserRank] = useState(null);
  const [isLoadingLeaderboard, setIsLoadingLeaderboard] = useState(false);

  // ✅ 新增：总词汇数
  const [totalWordsCount, setTotalWordsCount] = useState(0);

  // 修复：正确计算每个类别的单词数量
  const getWordsForCategory = useCallback((categoryValue) => {
    return words.filter((w) => {
      if (Array.isArray(w.category)) {
        return w.category.some(cat => 
          String(cat).toLowerCase().trim() === categoryValue.toLowerCase().trim()
        );
      }
      return String(w.category || '').toLowerCase().trim() === categoryValue.toLowerCase().trim();
    });
  }, [words]);

  // 计算总体统计数据
  const totalMastered = useMemo(() => {
    const allMasteredWords = new Set();
    
    Object.values(masteredStats).forEach(stat => {
      // 只统计手动削词
      (stat.manualMasteredWords || []).forEach(word => allMasteredWords.add(word));
    });
    
    return allMasteredWords.size;
  }, [masteredStats]);
  
  // const totalWords = useMemo(() => {
  //   return Object.values(masteredStats).reduce((acc, stat) => 
  //     acc + (stat.totalWords || 0), 0
  //   );
  // }, [masteredStats]);
  const totalWords = totalWordsCount;

  // const overallProgress = useMemo(() => {
  //   return totalWords > 0 ? Math.round((totalMastered / totalWords) * 100) : 0;
  // }, [totalWords, totalMastered]);

  // ✅ overallProgress 也跟着用新的 totalWords
  const overallProgress = useMemo(() => {
    return totalWordsCount > 0 
      ? Math.round((totalMastered / totalWordsCount) * 100) 
      : 0;
  }, [totalMastered, totalWordsCount]);

  // 计算用户等级
  const calculateLevel = useCallback((masteredCount) => {
    return Math.floor(masteredCount / 100) + 1;
  }, []);

  // 获取等级称号
  const getLevelTitle = useCallback((level) => {
    if (level >= 100) return '传奇大师';
    if (level >= 80) return '至尊王者';
    if (level >= 60) return '钻石宗师';
    if (level >= 40) return '铂金精英';
    if (level >= 20) return '黄金学者';
    if (level >= 10) return '白银勇者';
    return '青铜学徒';
  }, []);

  // ✅ 修改：使用新的 API 获取累积掌握的单词
  const getCumulativeMasteredWords = useCallback(async (categoryValue) => {
    try {
      console.log(`📥 获取 ${categoryValue} 的累积削词列表`);
      const data = await progressAPI.getCumulativeMastered(categoryValue);
      
      console.log(`✅ 累积削词统计 - ${categoryValue}:`, {
        手动削词: Object.keys(data.manualMastered || {}).length,
        学习掌握: Object.keys(data.learnedMastered || {}).length
      });
      
      return {
        manualMastered: data.manualMastered || {},
        learnedMastered: data.learnedMastered || {}
      };
    } catch (err) {
      console.error(`❌ 获取累积削词列表失败: ${categoryValue}`, err);
      setError(`获取累积削词列表失败: ${err.message}`);
      return { manualMastered: {}, learnedMastered: {} };
    }
  }, []);

  // 获取每个类别的削词统计
  useEffect(() => {
    const fetchMasteredStats = async () => {
      if (!user) return;
      
      try {
        const stats = {};
        for (const cat of categories) {
          try {
            const { manualMastered, learnedMastered } = await getCumulativeMasteredWords(cat.value);
            const totalWordsInCategory = getWordsForCategory(cat.value).length;
            
            stats[cat.value] = {
              manualMasteredCount: Object.keys(manualMastered).length,
              learnedMasteredCount: Object.keys(learnedMastered).length,
              totalWords: totalWordsInCategory,
              manualMasteredWords: Object.keys(manualMastered),
              learnedMasteredWords: Object.keys(learnedMastered),
            };
          } catch (err) {
            console.error(`获取${cat.value}削词统计失败:`, err);
            stats[cat.value] = {
              manualMasteredCount: 0,
              learnedMasteredCount: 0,
              totalWords: getWordsForCategory(cat.value).length,
              manualMasteredWords: [],
              learnedMasteredWords: [],
            };
          }
        }
        setMasteredStats(stats);
        console.log('削词统计加载完成:', stats);
      } catch (err) {
        console.error('加载削词统计失败:', err);
        setError('加载学习统计失败，请检查网络连接');
      }
    };

    fetchMasteredStats();
  }, [user, categories, getCumulativeMasteredWords, getWordsForCategory]);

  // ✅ 修改：使用新的 API 加载排行榜
  useEffect(() => {
    const loadLeaderboard = async () => {
      if (!user) return;
      
      setIsLoadingLeaderboard(true);
      try {
        console.log('📥 加载排行榜数据...');
        const data = await progressAPI.getLeaderboard();
        
        // 计算等级
        const formattedData = data.map(userItem => ({
          ...userItem,
          level: calculateLevel(userItem.totalMastered)
        }));
        
        setLeaderboard(formattedData.slice(0, 10));
        
        // 找到当前用户的排名
        const userIndex = formattedData.findIndex(u => u.userId === user.uid);
        if (userIndex !== -1) {
          setCurrentUserRank(userIndex + 1);
        }
        
        console.log('✅ 排行榜加载成功，共', formattedData.length, '人');
      } catch (err) {
        console.error('❌ 加载排行榜失败:', err);
        setError('加载排行榜失败: ' + err.message);
      } finally {
        setIsLoadingLeaderboard(false);
      }
    };
    
    loadLeaderboard();
  }, [user, calculateLevel]);

  // ✅ 新增：获取总词汇数
  useEffect(() => {
    const fetchTotalWords = async () => {
      try {
        const response = await fetch('/api/words/version');
        const data = await response.json();
        setTotalWordsCount(data.total || 0);
      } catch (err) {
        console.error('获取总词汇数失败:', err);
        setTotalWordsCount(words.length > 0 ? words.length : 10869);
      }
    };
    fetchTotalWords();
  }, [words.length]);

  // 加载句子统计数据
  useEffect(() => {
    const fetchSentenceStats = async () => {
      if (!user) return;
      setIsLoadingSentenceStats(true);
      try {
        const data = await sentencesAPI.getStats();
        setSentenceStats(data);
      } catch (err) {
        console.error('加载句子统计失败:', err);
      } finally {
        setIsLoadingSentenceStats(false);
      }
    };
    fetchSentenceStats();
  }, [user]);

  // 加载微信绑定状态
  useEffect(() => {
    const checkWxBind = async () => {
      try {
        // const token = localStorage.getItem('token') || 
        //   document.cookie.match(/token=([^;]+)/)?.[1];
        const { getAuthToken } = require('./api');
        const res = await fetch('/api/auth/verify', {
          headers: { 'Authorization': `Bearer ${getAuthToken()}` }
        });
        const data = await res.json();
        setWxBindStatus(data.isWxUser ? 'bound' : 'unbound');
      } catch(e) {
        console.error('检查微信绑定状态失败:', e);
      }
    };
    if (user) checkWxBind();
  }, [user]);

  const showAccountMsg = (text, type = 'success') => {
    setAccountMsg({ text, type });
    setTimeout(() => setAccountMsg({ text: '', type: '' }), 3000);
  };

  // 修改密码
  const handleChangePassword = async () => {
    if (!oldPassword || !newPassword || !confirmPassword) {
      return showAccountMsg('请填写所有字段', 'error');
    }
    if (newPassword !== confirmPassword) {
      return showAccountMsg('两次密码不一致', 'error');
    }
    if (newPassword.length < 6) {
      return showAccountMsg('新密码至少6位', 'error');
    }
    setIsAccountLoading(true);
    try {
      const { getAuthToken } = require('./api');
      const res = await fetch('/api/auth/change-password', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAuthToken()}`
        },
        body: JSON.stringify({ oldPassword, newPassword }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      showAccountMsg('密码修改成功！');
      setOldPassword(''); setNewPassword(''); setConfirmPassword('');
    } catch(e) {
      showAccountMsg(e.message, 'error');
    } finally {
      setIsAccountLoading(false);
    }
  };

  // 绑定邮箱（微信用户）
  const handleBindEmail = async () => {
    if (!bindEmail || !bindEmailPassword) {
      return showAccountMsg('请填写邮箱和密码', 'error');
    }
    if (bindEmailPassword.length < 6) {
      return showAccountMsg('密码至少6位', 'error');
    }
    setIsAccountLoading(true);
    try {
      const { getAuthToken } = require('./api');
      const res = await fetch('/api/auth/bind-email', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAuthToken()}`
        },
        body: JSON.stringify({ email: bindEmail, password: bindEmailPassword }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      showAccountMsg('邮箱绑定成功！');
      setBindEmail(''); setBindEmailPassword('');
    } catch(e) {
      showAccountMsg(e.message, 'error');
    } finally {
      setIsAccountLoading(false);
    }
  };

  // 解绑微信
  const handleUnbindWx = async () => {
    if (!window.confirm('确定解绑微信？解绑后需用邮箱密码登录')) return;
    setIsAccountLoading(true);
    try {
      const { getAuthToken } = require('./api');
      const res = await fetch('/api/auth/wx-unbind', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getAuthToken()}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      setWxBindStatus('unbound');
      showAccountMsg('微信解绑成功！');
    } catch(e) {
      showAccountMsg(e.message, 'error');
    } finally {
      setIsAccountLoading(false);
    }
  };

  // 注销账号
  const handleDeleteAccount = async () => {
    const confirmed = window.confirm(
      '⚠️ 警告！注销账号将永久删除所有学习数据，无法恢复！\n\n确定要注销账号吗？'
    );
    if (!confirmed) return;
    const reconfirmed = window.confirm('再次确认：真的要永久注销账号吗？');
    if (!reconfirmed) return;
    setIsAccountLoading(true);
    try {
      const { getAuthToken } = require('./api');
      const res = await fetch('/api/auth/delete-account', {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${getAuthToken()}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      alert('账号已注销，即将返回首页');
      window.location.reload();
    } catch(e) {
      showAccountMsg(e.message, 'error');
    } finally {
      setIsAccountLoading(false);
    }
  };

  // 清理预览URL
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('文件大小不能超过5MB');
        return;
      }
      if (!file.type.startsWith('image/')) {
        setError('请上传图片文件');
        return;
      }
      
      setSelectedFile(file);
      setError(null);
      setSuccess(null);
      
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      const newPreviewUrl = URL.createObjectURL(file);
      setPreviewUrl(newPreviewUrl);
    }
  };

  // ✅ 修改：使用新的 API 更新资料（暂时不支持头像上传）
  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    if (!newDisplayName.trim()) {
      setError('昵称不能为空');
      return;
    }

    setIsUploading(true);
    setError(null);
    setSuccess(null);
    
    try {
      let photoURL = userProfile.photoURL;

      // ✅ 处理头像上传
      if (selectedFile) {
        console.log('📤 开始处理头像上传...');
        
        // 检查文件大小（限制 2MB）
        if (selectedFile.size > 2 * 1024 * 1024) {
          throw new Error('图片大小不能超过 2MB');
        }

        // 转换为 Base64
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(selectedFile);
        });

        photoURL = base64;
        console.log('✅ 头像转换完成，大小:', Math.round(base64.length / 1024), 'KB');
      }

      console.log('📤 更新用户资料...');
      await progressAPI.updateProfile(newDisplayName.trim(), photoURL);

      setUserProfile({
        displayName: newDisplayName.trim(),
        photoURL: photoURL,
      });
      
      setSelectedFile(null);
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
      }
      
      setSuccess('资料更新成功！');
      console.log('✅ 资料更新成功');
      
    } catch (err) {
      console.error('❌ 用户资料更新失败:', err);
      setError(`更新失败: ${err.message}`);
    } finally {
      setIsUploading(false);
    }
  };

  // ✅ 修改：重置类别（已经在 App.js 中使用后端 API）
  const handleResetCategory = async (categoryValue, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const firstConfirm = window.confirm(
      `⚠️ 警告：重置将删除 ${categoryValue} 类别的所有学习数据！\n\n这包括：\n- 所有学习进度\n- 手动削词记录\n- 学习掌握记录\n\n此操作不可恢复，请谨慎操作！\n\n点击"确定"继续`
    );
    
    if (!firstConfirm) return;
    
    const userInput = window.prompt(`请输入单词 "sure" 来确认重置操作：`);
    
    if (userInput !== 'sure') {
      if (userInput !== null) {
        alert('输入错误，重置已取消');
      }
      return;
    }
    
    setIsResetting(true);
    setResettingCategory(categoryValue);
    setError(null);
    
    try {
      await onResetCategory(categoryValue);
      
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const { manualMastered, learnedMastered } = await getCumulativeMasteredWords(categoryValue);
      setMasteredStats(prev => ({
        ...prev,
        [categoryValue]: {
          ...prev[categoryValue],
          manualMasteredWords: Object.keys(manualMastered),
          learnedMasteredWords: Object.keys(learnedMastered),
          manualMasteredCount: Object.keys(manualMastered).length,
          learnedMasteredCount: Object.keys(learnedMastered).length,
        }
      }));
      
      setSuccess(`${categoryValue} 类别已成功重置`);
    } catch (err) {
      console.error('重置失败:', err);
      setError('重置失败，请重试');
    } finally {
      setIsResetting(false);
      setResettingCategory(null);
    }
  };

  // ✅ 修改：使用新的 API 释放单词
  const releaseWord = async (categoryValue, word) => {
    if (!window.confirm(`确定要释放单词 "${word}" 吗？释放后可以重新学习该单词。`)) {
      return;
    }
    
    setIsReleasing(true);
    setReleasingWord(`${categoryValue}-${word}`);
    setError(null);
    
    try {
      console.log(`📤 释放单词: ${word} (${categoryValue})`);
      await progressAPI.releaseWord(categoryValue, word);
      
      // 重新获取数据
      const { manualMastered, learnedMastered } = await getCumulativeMasteredWords(categoryValue);
      setMasteredStats(prev => ({
        ...prev,
        [categoryValue]: {
          ...prev[categoryValue],
          manualMasteredWords: Object.keys(manualMastered),
          learnedMasteredWords: Object.keys(learnedMastered),
          manualMasteredCount: Object.keys(manualMastered).length,
          learnedMasteredCount: Object.keys(learnedMastered).length,
        }
      }));
      
      setSuccess(`单词 "${word}" 已释放`);
      console.log('✅ 单词释放成功');
    } catch (err) {
      console.error('❌ 释放单词失败:', err);
      setError('释放失败，请重试: ' + err.message);
    } finally {
      setIsReleasing(false);
      setReleasingWord(null);
    }
  };

  // 生成分享图片
  const generateShareImage = async () => {
    setIsGeneratingImage(true);
    try {
      const canvas = document.createElement('canvas');
      canvas.width = 1200;
      canvas.height = 630;
      const ctx = canvas.getContext('2d');

      // 背景渐变
      const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
      gradient.addColorStop(0, '#667eea');
      gradient.addColorStop(1, '#764ba2');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // 添加装饰圆圈
      ctx.fillStyle = 'rgba(255, 255, 255, 0.1)';
      ctx.beginPath();
      ctx.arc(100, 100, 150, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(1100, 530, 200, 0, Math.PI * 2);
      ctx.fill();

      // 标题
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 60px Arial, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('削词侠 学习成果', canvas.width / 2, 100);

      // 用户信息
      ctx.font = '32px Arial, sans-serif';
      ctx.fillText(`${userProfile.displayName || '学习者'}`, canvas.width / 2, 160);

      // 统计卡片背景
      const cardY = 220;
      const cardHeight = 280;
      ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
      ctx.shadowColor = 'rgba(0, 0, 0, 0.2)';
      ctx.shadowBlur = 20;
      ctx.shadowOffsetY = 10;
      ctx.fillRect(100, cardY, canvas.width - 200, cardHeight);
      ctx.shadowBlur = 0;
      ctx.shadowOffsetY = 0;

      // 统计数据
      const stats = [
        { label: '总词汇量', value: totalWords, icon: '📚', color: '#667eea' },
        { label: '已掌握', value: totalMastered, icon: '🏆', color: '#f093fb' },
        { label: '掌握率', value: `${overallProgress}%`, icon: '🎯', color: '#4facfe' }
      ];

      const cardPadding = 80;
      const statWidth = (canvas.width - 200 - cardPadding * 2) / 3;
      
      stats.forEach((stat, index) => {
        const x = 100 + cardPadding + index * statWidth + statWidth / 2;
        const y = cardY + 80;

        // 图标
        ctx.font = '56px Arial, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(stat.icon, x, y);

        // 数值
        ctx.font = 'bold 48px Arial, sans-serif';
        ctx.fillStyle = stat.color;
        ctx.fillText(String(stat.value), x, y + 80);

        // 标签
        ctx.font = '24px Arial, sans-serif';
        ctx.fillStyle = '#666666';
        ctx.fillText(stat.label, x, y + 120);
      });

      // 网址
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 28px Arial, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('xiaocixia.cn', canvas.width / 2, canvas.height - 40);

      // 转换为blob
      return new Promise((resolve) => {
        canvas.toBlob((blob) => {
          resolve(blob);
        }, 'image/png');
      });
    } finally {
      setIsGeneratingImage(false);
    }
  };

  // 分享到社交平台
  const shareToSocial = async (platform) => {
    try {
      const imageBlob = await generateShareImage();
      const imageUrl = URL.createObjectURL(imageBlob);
      
      const shareText = `我在削词侠已掌握${totalMastered}个单词，掌握率${overallProgress}%！一起来学习吧！`;
      const websiteUrl = 'https://xiaocixia.cn';  // ✅ 修改网址

      // 社交平台发布页面 URL
      const platformUrls = {
        weibo: 'https://weibo.com/compose/publish',  // 微博发布页
        douyin: 'https://www.douyin.com',  // 抖音（需要App）
        channels: 'https://channels.weixin.qq.com',  // 视频号（需要微信）
        kuaishou: 'https://www.kuaishou.com',  // 快手（需要App）
        xiaohongshu: 'https://www.xiaohongshu.com/explore'  // 小红书
      };

      switch (platform) {
        case 'weibo': {
          // 下载图片
          const link = document.createElement('a');
          link.href = imageUrl;
          link.download = `xiaocixia-progress-${Date.now()}.png`;
          link.click();
          
          // 延迟打开微博发布页
          setTimeout(() => {
            window.open(platformUrls.weibo, '_blank');
          }, 500);
          
          URL.revokeObjectURL(imageUrl);
          setSuccess('图片已下载，微博发布页已打开！');
          break;
        }
        
        case 'douyin': {
          const link = document.createElement('a');
          link.href = imageUrl;
          link.download = `xiaocixia-progress-${Date.now()}.png`;
          link.click();
          
          setTimeout(() => {
            window.open(platformUrls.douyin, '_blank');
          }, 500);
          
          URL.revokeObjectURL(imageUrl);
          setSuccess('图片已下载，请在抖音App中上传！');
          break;
        }
        
        case 'channels': {
          const link = document.createElement('a');
          link.href = imageUrl;
          link.download = `xiaocixia-progress-${Date.now()}.png`;
          link.click();
          
          setTimeout(() => {
            window.open(platformUrls.channels, '_blank');
          }, 500);
          
          URL.revokeObjectURL(imageUrl);
          setSuccess('图片已下载，请在微信视频号中上传！');
          break;
        }
        
        case 'kuaishou': {
          const link = document.createElement('a');
          link.href = imageUrl;
          link.download = `xiaocixia-progress-${Date.now()}.png`;
          link.click();
          
          setTimeout(() => {
            window.open(platformUrls.kuaishou, '_blank');
          }, 500);
          
          URL.revokeObjectURL(imageUrl);
          setSuccess('图片已下载，请在快手App中上传！');
          break;
        }
        
        case 'xiaohongshu': {
          const link = document.createElement('a');
          link.href = imageUrl;
          link.download = `xiaocixia-progress-${Date.now()}.png`;
          link.click();
          
          setTimeout(() => {
            window.open(platformUrls.xiaohongshu, '_blank');
          }, 500);
          
          URL.revokeObjectURL(imageUrl);
          setSuccess('图片已下载，小红书页面已打开！');
          break;
        }
        
        case 'download': {
          const link = document.createElement('a');
          link.href = imageUrl;
          link.download = `xiaocixia-progress-${Date.now()}.png`;
          link.click();
          URL.revokeObjectURL(imageUrl);
          setSuccess('图片已下载！');
          break;
        }
        
        default: {
          // 系统分享（如果支持）
          if (navigator.share && navigator.canShare) {
            const file = new File([imageBlob], 'xiaocixia-progress.png', { type: 'image/png' });
            if (navigator.canShare({ files: [file] })) {
              await navigator.share({
                title: '削词侠学习成果',
                text: shareText,
                url: websiteUrl,
                files: [file]
              });
            } else {
              await navigator.share({
                title: '削词侠学习成果',
                text: shareText,
                url: websiteUrl
              });
            }
          } else {
            const link = document.createElement('a');
            link.href = imageUrl;
            link.download = `xiaocixia-progress-${Date.now()}.png`;
            link.click();
          }
          URL.revokeObjectURL(imageUrl);
          setSuccess('分享成功！');
          break;
        }
      }
    } catch (err) {
      console.error('分享失败:', err);
      setError('分享失败，请重试');
    }
  };

  const displayAvatar = previewUrl || userProfile.photoURL || 
    `https://ui-avatars.com/api/?name=${encodeURIComponent(
      (userProfile.displayName || user.email).charAt(0)
    )}&size=100&background=1890ff&color=fff`;

  return (
    <div className="personal-center">
      <div className="personal-center-header">
        <button onClick={onBack} className="back-button">
          ← 返回学习
        </button>
        <h2>个人中心</h2>
        <button
          onClick={() => { setShowAccountModal(true); setAccountTab('wx'); setAccountMsg({ text: '', type: '' }); }}
          style={{
            padding: '8px 16px',
            background: 'rgba(255,255,255,0.2)',
            color: 'white',
            border: '1px solid rgba(255,255,255,0.4)',
            borderRadius: '20px',
            cursor: 'pointer',
            fontSize: '0.9em',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          ⚙️ 账号设置
        </button>
      </div>
      
      {error && (
        <div className="message error-message">
          {error}
        </div>
      )}
      
      {success && (
        <div className="message success-message">
          ✓ {success}
        </div>
      )}

      {/* 个人等级卡片 */}
      <div className="user-level-card">
        <div className="level-badge">
          <div className="level-number">LV.{calculateLevel(totalMastered)}</div>
          <div className="level-title">{getLevelTitle(calculateLevel(totalMastered))}</div>
        </div>
        <div className="level-info">
          <div className="level-stat">
            <span className="stat-label">已掌握</span>
            <span className="stat-value">{totalMastered} 词</span>
          </div>
          <div className="level-stat">
            <span className="stat-label">当前排名</span>
            <span className="stat-value rank-highlight">
              {currentUserRank ? `第 ${currentUserRank} 名` : '未上榜'}
            </span>
          </div>
          <div className="level-stat">
            <span className="stat-label">下一级</span>
            <span className="stat-value">{100 - (totalMastered % 100)} 词</span>
          </div>
        </div>
        <div className="level-progress">
          <div className="level-progress-bar">
            <div 
              className="level-progress-fill" 
              style={{ width: `${(totalMastered % 100)}%` }}
            />
          </div>
          <div className="level-progress-text">
            {totalMastered % 100} / 100
          </div>
        </div>
      </div>

      {/* 排行榜 */}
      <div className="leaderboard-section">
        <h3>
          <span className="leaderboard-icon">🏆</span>
          学习排行榜
          <span className="leaderboard-subtitle">TOP 10</span>
        </h3>
        
        {isLoadingLeaderboard ? (
          <div className="leaderboard-loading">
            <div className="loading-spinner" />
            <p>加载排行榜中...</p>
          </div>
        ) : (
          <div className="leaderboard-list">
            {leaderboard.map((userItem, index) => {
              const isCurrentUser = userItem.userId === user.uid;
              const rank = index + 1;
              
              return (
                <div 
                  key={userItem.userId} 
                  className={`leaderboard-item ${isCurrentUser ? 'current-user' : ''} ${rank <= 3 ? 'top-three' : ''}`}
                >
                  <div className="rank-badge">
                    {rank === 1 && <span className="medal gold">🥇</span>}
                    {rank === 2 && <span className="medal silver">🥈</span>}
                    {rank === 3 && <span className="medal bronze">🥉</span>}
                    {rank > 3 && <span className="rank-number">{rank}</span>}
                  </div>
                  
                  <img 
                    src={userItem.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent((userItem.displayName || '用').charAt(0))}&size=100&background=1890ff&color=fff`}
                    alt={userItem.displayName}
                    className="leaderboard-avatar"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent((userItem.displayName || '用').charAt(0))}&size=100&background=1890ff&color=fff`;
                    }}
                  />
                  
                  <div className="leaderboard-user-info">
                    <div className="user-name">
                      {userItem.displayName}
                      {isCurrentUser && <span className="you-badge">（你）</span>}
                    </div>
                    <div className="user-level">
                      Lv.{userItem.level} · {getLevelTitle(userItem.level)}
                    </div>
                  </div>
                  
                  <div className="leaderboard-stats">
                    <div className="mastered-count">{userItem.totalMastered}</div>
                    <div className="mastered-label">已掌握</div>
                  </div>
                </div>
              );
            })}
            
            {leaderboard.length === 0 && (
              <div className="leaderboard-empty">
                <p>暂无排行数据</p>
                <p className="empty-hint">开始学习，争夺排行榜首位！</p>
              </div>
            )}
          </div>
        )}
      </div>
      
      <div className="main-content">
        <div className="profile-section">
          <div className="avatar-section">
            <img
              src={displayAvatar}
              alt="头像"
              className="avatar"
            />
            {previewUrl && (
              <p className="preview-text">预览新头像</p>
            )}
            <input
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="file-input"
              disabled={isUploading}
            />
            <p style={{ fontSize: '0.85em', color: '#999', marginTop: '5px' }}>
              支持 JPG、PNG 格式，大小不超过 2MB
            </p>
          </div>
          
          <form onSubmit={handleUpdateProfile} className="profile-form">
            <div className="form-group">
              <label>昵称</label>
              <input
                type="text"
                value={newDisplayName}
                onChange={(e) => setNewDisplayName(e.target.value)}
                placeholder="输入昵称"
                className="form-input"
                disabled={isUploading}
              />
            </div>
            
            <div className="form-group">
              <label>邮箱</label>
              <input
                type="email"
                value={user.email}
                className="form-input disabled"
                disabled
              />
            </div>
            
            <button
              type="submit"
              disabled={
                isUploading || 
                (newDisplayName.trim() === userProfile.displayName && !selectedFile)  // ✅ 修改：头像改变也可以保存
              }
              className="save-button"
            >
              {isUploading ? (
                <>
                  <div className="loading-spinner" />
                  保存中...
                </>
              ) : (
                '保存更改'
              )}
            </button>
          </form>
        </div>

        <div className="stats-section">
          <div className="stats-grid">
            <div className="stat-card total-words">
              <div className="stat-icon">📚</div>
              <div className="stat-content">
                <div className="stat-number">{totalWords}</div>
                <div className="stat-label">总词汇量</div>
              </div>
            </div>
            
            <div className="stat-card mastered-words">
              <div className="stat-icon">🏆</div>
              <div className="stat-content">
                <div className="stat-number">{totalMastered}</div>
                <div className="stat-label">已掌握</div>
              </div>
            </div>
            
            <div className="stat-card progress-rate">
              <div className="stat-icon">🎯</div>
              <div className="stat-content">
                <div className="stat-number">{overallProgress}%</div>
                <div className="stat-label">掌握率</div>
              </div>
            </div>
          </div>
          
          {/* 社交分享按钮 */}
          <div className="share-section" style={{ 
            marginTop: '30px', 
            padding: '20px', 
            background: '#f8f9fa', 
            borderRadius: '12px',
            border: '1px solid #e9ecef'
          }}>
            <h4 style={{ 
              marginBottom: '15px', 
              color: '#333', 
              fontSize: '1.1em',
              textAlign: 'center'
            }}>
              分享学习成果
            </h4>
            <div className="share-buttons" style={{
              display: 'flex',
              gap: '10px',
              justifyContent: 'center',
              flexWrap: 'wrap'
            }}>
              {/* 微博 */}
              <button
                onClick={() => shareToSocial('weibo')}
                disabled={isGeneratingImage}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 20px',
                  background: '#E6162D',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: isGeneratingImage ? 'not-allowed' : 'pointer',
                  fontSize: '0.9em',
                  opacity: isGeneratingImage ? 0.6 : 1,
                  transition: 'all 0.3s'
                }}
                title="分享到微博"
              >
                <span style={{ fontSize: '1.2em' }}>📢</span>
                微博
              </button>

              {/* 抖音 */}
              <button
                onClick={() => shareToSocial('douyin')}
                disabled={isGeneratingImage}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 20px',
                  background: 'linear-gradient(135deg, #00F2FE 0%, #4FACFE 50%, #00F2FE 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: isGeneratingImage ? 'not-allowed' : 'pointer',
                  fontSize: '0.9em',
                  opacity: isGeneratingImage ? 0.6 : 1,
                  transition: 'all 0.3s'
                }}
                title="分享到抖音"
              >
                <span style={{ fontSize: '1.2em' }}>🎵</span>
                抖音
              </button>

              {/* 视频号 */}
              <button
                onClick={() => shareToSocial('channels')}
                disabled={isGeneratingImage}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 20px',
                  background: '#07C160',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: isGeneratingImage ? 'not-allowed' : 'pointer',
                  fontSize: '0.9em',
                  opacity: isGeneratingImage ? 0.6 : 1,
                  transition: 'all 0.3s'
                }}
                title="分享到视频号"
              >
                <span style={{ fontSize: '1.2em' }}>📹</span>
                视频号
              </button>

              {/* 快手 */}
              <button
                onClick={() => shareToSocial('kuaishou')}
                disabled={isGeneratingImage}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 20px',
                  background: '#FF6600',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: isGeneratingImage ? 'not-allowed' : 'pointer',
                  fontSize: '0.9em',
                  opacity: isGeneratingImage ? 0.6 : 1,
                  transition: 'all 0.3s'
                }}
                title="分享到快手"
              >
                <span style={{ fontSize: '1.2em' }}>⚡</span>
                快手
              </button>

              {/* 小红书 */}
              <button
                onClick={() => shareToSocial('xiaohongshu')}
                disabled={isGeneratingImage}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 20px',
                  background: '#FF2442',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: isGeneratingImage ? 'not-allowed' : 'pointer',
                  fontSize: '0.9em',
                  opacity: isGeneratingImage ? 0.6 : 1,
                  transition: 'all 0.3s'
                }}
                title="分享到小红书"
              >
                <span style={{ fontSize: '1.2em' }}>📖</span>
                小红书
              </button>
              
              {/* 下载图片 */}
              <button
                onClick={() => shareToSocial('download')}
                disabled={isGeneratingImage}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 20px',
                  background: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: isGeneratingImage ? 'not-allowed' : 'pointer',
                  fontSize: '0.9em',
                  opacity: isGeneratingImage ? 0.6 : 1,
                  transition: 'all 0.3s'
                }}
                title="下载图片"
              >
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3"/>
                </svg>
                下载图片
              </button>
              
              <button
                onClick={() => shareToSocial('share')}
                disabled={isGeneratingImage}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 20px',
                  background: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: isGeneratingImage ? 'not-allowed' : 'pointer',
                  fontSize: '0.9em',
                  opacity: isGeneratingImage ? 0.6 : 1,
                  transition: 'all 0.3s'
                }}
                title="更多分享选项"
              >
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
                  <path d="M8.59 13.51l6.83 3.98M15.41 6.51l-6.82 3.98"/>
                </svg>
                更多
              </button>
            </div>
            {isGeneratingImage && (
              <p style={{ 
                marginTop: '10px', 
                color: '#666', 
                fontSize: '0.9em',
                textAlign: 'center'
              }}>
                正在生成分享图片...
              </p>
            )}
          </div>
        </div>
      </div>
      
      <div className="progress-section">
        <h3>学单词进度</h3>
        <div className="categories-grid">
          {categories.map((cat) => {
            const stats = masteredStats[cat.value] || {};
            
            // 只统计手动削词
            const manualWords = new Set(stats.manualMasteredWords || []);
            const actualMasteredCount = manualWords.size;
            
            // 修正进度计算
            const progress = stats.totalWords > 0 
              ? Math.min(100, Math.round((actualMasteredCount / stats.totalWords) * 100))
              : 0;
            
            const isExpanded = expandedCategory === cat.value;
            
            return (
              <div key={cat.value} className="category-card">
                <div className="category-header">
                  <h4>{cat.label}</h4>
                  <div className="category-actions">
                    <button
                      onClick={() => setExpandedCategory(isExpanded ? null : cat.value)}
                      className="expand-button"
                    >
                      {isExpanded ? '收起' : '展开'}
                    </button>
                    <button
                      onClick={(e) => handleResetCategory(cat.value, e)}
                      className="reset-button"
                      type="button"
                      disabled={isResetting}
                    >
                      {isResetting && resettingCategory === cat.value ? (
                        <>
                          <div className="mini-spinner" style={{ marginRight: '5px' }}></div>
                          重置中...
                        </>
                      ) : (
                        '重置'
                      )}
                    </button>
                  </div>
                </div>
                
                <div className="category-stats">
                  <div className="stat-item">
                    <span className="stat-value">{completedCounts[cat.value] || 0}</span>
                    <span className="stat-desc">学习次数</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-value">{manualWords.size}</span>
                    <span className="stat-desc">已掌握</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-value">{stats.totalWords || 0}</span>
                    <span className="stat-desc">总单词数</span>
                  </div>
                </div>
                
                <div className="progress-bar">
                  <div 
                    className="progress-fill" 
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
                <div className="progress-text">{progress}% 完成</div>

                {/* 👇 添加这部分展开内容 */}
                {isExpanded && (
                  <div className="expanded-words">
                    {/* 手动削词列表 */}
                    {manualWords.size > 0 && (
                      <div className="words-section">
                        <h5 className="words-title">
                          🏆 已掌握单词 ({manualWords.size})
                        </h5>
                        <div className="words-list">
                          {Array.from(manualWords).map((word, index) => {
                            const wordKey = `${cat.value}-${word}`;
                            const isReleasingThis = isReleasing && releasingWord === wordKey;
                            
                            return (
                              <span key={`manual-${index}`} className="word-tag manual">
                                {word}
                                {isReleasingThis ? (
                                  <div className="word-releasing-spinner" title="释放中...">
                                    <div className="mini-spinner"></div>
                                  </div>
                                ) : (
                                  <button
                                    className="word-release-btn"
                                    onClick={() => releaseWord(cat.value, word, 'manual')}
                                    title="释放该单词"
                                    disabled={isReleasing}
                                  >
                                    ×
                                  </button>
                                )}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    
                    {/* 学习掌握列表 */}
                    {manualWords.size === 0 && (
                      <div className="no-words">
                        <p>该级别还没有掌握的单词</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* 学短句统计 */}
        <div style={{ marginTop: '30px' }}>
          <h3>学句子进度</h3>
          {isLoadingSentenceStats ? (
            <p style={{ color: '#999' }}>加载中...</p>
          ) : sentenceStats && sentenceStats.totalSentences > 0 ? (
            <div>
              {/* 总体统计 */}
              <div className="category-stats" style={{ gridTemplateColumns: 'repeat(3, 1fr)', marginBottom: '20px' }}>
                <div className="stat-item">
                  <span className="stat-value">{sentenceStats.totalSentences}</span>
                  <span className="stat-desc">句子总数</span>
                </div>
                <div className="stat-item">
                  <span className="stat-value">{sentenceStats.masteredSentences}</span>
                  <span className="stat-desc">已削句</span>
                </div>
                <div className="stat-item">
                  <span className="stat-value">
                    {sentenceStats.totalSentences > 0
                      ? Math.round((sentenceStats.masteredSentences / sentenceStats.totalSentences) * 100)
                      : 0}%
                  </span>
                  <span className="stat-desc">掌握率</span>
                </div>
              </div>

              {/* 按分类统计 */}
              {sentenceStats.bySource && sentenceStats.bySource.length > 0 && (
                <div className="categories-grid">
                  {sentenceStats.bySource.map((item) => (
                    <div key={item.source} className="category-card">
                      <div className="category-header">
                        <h4>{item.source}</h4>
                      </div>
                      <div className="category-stats">
                        <div className="stat-item">
                          <span className="stat-value">{item.mastered_count}</span>
                          <span className="stat-desc">已削句</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <p style={{ color: '#999', padding: '20px', textAlign: 'center' }}>
              暂无学短句数据，快去学几句吧！
            </p>
          )}
        </div>
      </div>

      {/* 账号设置弹窗 */}
      {showAccountModal && (
        <div
          onClick={() => setShowAccountModal(false)}
          style={{
            position: 'fixed', inset: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: '16px',
              padding: '32px',
              width: '90vw',
              maxWidth: '460px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
              position: 'relative',
              maxHeight: '90vh',
              overflowY: 'auto',
            }}
          >
            {/* 关闭按钮 */}
            <button
              onClick={() => setShowAccountModal(false)}
              style={{
                position: 'absolute', top: '16px', right: '16px',
                background: 'none', border: 'none', fontSize: '1.2em',
                cursor: 'pointer', color: '#999',
              }}
            >✕</button>

            <h3 style={{ margin: '0 0 20px', color: '#333', fontSize: '1.2em' }}>
              ⚙️ 账号设置
            </h3>

            {/* 标签页 */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', flexWrap: 'wrap' }}>
              {[
                { key: 'wx', label: '💬 微信绑定' },
                { key: 'password', label: '🔑 修改密码' },
                { key: 'email', label: '📧 绑定邮箱' },
                { key: 'delete', label: '🗑️ 注销账号' },
              ].map(tab => (
                <button key={tab.key} onClick={() => { setAccountTab(tab.key); setAccountMsg({ text: '', type: '' }); }} style={{
                  padding: '7px 14px',
                  border: 'none',
                  borderRadius: '20px',
                  cursor: 'pointer',
                  fontSize: '0.85em',
                  fontWeight: accountTab === tab.key ? '600' : '400',
                  background: accountTab === tab.key ? '#1890ff' : '#f5f5f5',
                  color: accountTab === tab.key ? 'white' : '#666',
                  transition: 'all 0.2s',
                }}>
                  {tab.label}
                </button>
              ))}
            </div>

            {/* 消息提示 */}
            {accountMsg.text && (
              <div style={{
                padding: '10px 14px', borderRadius: '8px', marginBottom: '16px', fontSize: '0.9em',
                background: accountMsg.type === 'error' ? '#fff2f0' : '#f6ffed',
                color: accountMsg.type === 'error' ? '#ff4d4f' : '#52c41a',
                border: `1px solid ${accountMsg.type === 'error' ? '#ffccc7' : '#b7eb8f'}`,
              }}>
                {accountMsg.text}
              </div>
            )}

            {/* 微信绑定 */}
            {accountTab === 'wx' && (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '16px', background: '#f9f9f9', borderRadius: '12px', marginBottom: '20px' }}>
                  <span style={{ fontSize: '2.5em' }}>💬</span>
                  <div>
                    <p style={{ margin: 0, fontWeight: '600', color: '#333' }}>微信账号</p>
                    <p style={{ margin: '4px 0 0', fontSize: '0.85em', color: wxBindStatus === 'bound' ? '#52c41a' : '#999' }}>
                      {wxBindStatus === null ? '检测中...' : wxBindStatus === 'bound' ? '✅ 已绑定微信' : '❌ 未绑定微信'}
                    </p>
                  </div>
                </div>
                {wxBindStatus === 'bound' ? (
                  <button onClick={handleUnbindWx} disabled={isAccountLoading} style={{
                    width: '100%', padding: '12px', background: '#ff4d4f', color: 'white',
                    border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '1em', fontWeight: '600',
                    opacity: isAccountLoading ? 0.6 : 1,
                  }}>
                    {isAccountLoading ? '处理中...' : '解绑微信'}
                  </button>
                ) : (
                  <button onClick={async () => {
                    try {
                      const res = await fetch('/api/auth/wx-web-url');
                      const data = await res.json();
                      window.location.href = data.url;
                    } catch(e) {
                      showAccountMsg('获取微信授权失败', 'error');
                    }
                  }} style={{
                    width: '100%', padding: '12px', background: '#07C160', color: 'white',
                    border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '1em', fontWeight: '600',
                  }}>
                    💬 绑定微信账号
                  </button>
                )}
              </div>
            )}

            {/* 修改密码 */}
            {accountTab === 'password' && (
              <div>
                {[
                  { label: '当前密码', value: oldPassword, setter: setOldPassword },
                  { label: '新密码（至少6位）', value: newPassword, setter: setNewPassword },
                  { label: '确认新密码', value: confirmPassword, setter: setConfirmPassword },
                ].map((field, i) => (
                  <div key={i} style={{ marginBottom: '14px' }}>
                    <label style={{ display: 'block', fontSize: '0.85em', color: '#666', marginBottom: '6px' }}>
                      {field.label}
                    </label>
                    <input
                      type="password"
                      value={field.value}
                      onChange={(e) => field.setter(e.target.value)}
                      style={{
                        width: '100%', padding: '10px 14px', boxSizing: 'border-box',
                        border: '1px solid #ddd', borderRadius: '8px',
                        fontSize: '0.95em', outline: 'none',
                      }}
                    />
                  </div>
                ))}
                <button onClick={handleChangePassword} disabled={isAccountLoading} style={{
                  width: '100%', padding: '12px', background: '#1890ff', color: 'white',
                  border: 'none', borderRadius: '8px', cursor: 'pointer',
                  fontSize: '1em', fontWeight: '600', opacity: isAccountLoading ? 0.6 : 1,
                }}>
                  {isAccountLoading ? '修改中...' : '确认修改密码'}
                </button>
              </div>
            )}

            {/* 绑定邮箱 */}
            {accountTab === 'email' && (
              <div>
                <p style={{ fontSize: '0.85em', color: '#999', marginBottom: '16px', lineHeight: '1.6' }}>
                  微信账号绑定邮箱后，即使微信解绑也可用邮箱密码登录，学习数据不会丢失。
                </p>
                {[
                  { label: '邮箱地址', value: bindEmail, setter: setBindEmail, type: 'email' },
                  { label: '设置密码（至少6位）', value: bindEmailPassword, setter: setBindEmailPassword, type: 'password' },
                ].map((field, i) => (
                  <div key={i} style={{ marginBottom: '14px' }}>
                    <label style={{ display: 'block', fontSize: '0.85em', color: '#666', marginBottom: '6px' }}>
                      {field.label}
                    </label>
                    <input
                      type={field.type}
                      value={field.value}
                      onChange={(e) => field.setter(e.target.value)}
                      style={{
                        width: '100%', padding: '10px 14px', boxSizing: 'border-box',
                        border: '1px solid #ddd', borderRadius: '8px',
                        fontSize: '0.95em', outline: 'none',
                      }}
                    />
                  </div>
                ))}
                <button onClick={handleBindEmail} disabled={isAccountLoading} style={{
                  width: '100%', padding: '12px', background: '#1890ff', color: 'white',
                  border: 'none', borderRadius: '8px', cursor: 'pointer',
                  fontSize: '1em', fontWeight: '600', opacity: isAccountLoading ? 0.6 : 1,
                }}>
                  {isAccountLoading ? '绑定中...' : '绑定邮箱'}
                </button>
              </div>
            )}

            {/* 注销账号 */}
            {accountTab === 'delete' && (
              <div>
                <div style={{
                  background: '#fff2f0', border: '1px solid #ffccc7',
                  borderRadius: '12px', padding: '16px', marginBottom: '20px',
                }}>
                  <p style={{ margin: 0, color: '#ff4d4f', fontWeight: '600', fontSize: '1em' }}>
                    ⚠️ 危险操作
                  </p>
                  <p style={{ margin: '8px 0 0', fontSize: '0.85em', color: '#666', lineHeight: '1.6' }}>
                    注销账号将永久删除所有学习进度、削词记录和账号信息，<strong>无法恢复</strong>。
                  </p>
                </div>
                <button onClick={handleDeleteAccount} disabled={isAccountLoading} style={{
                  width: '100%', padding: '12px', background: '#ff4d4f', color: 'white',
                  border: 'none', borderRadius: '8px', cursor: 'pointer',
                  fontSize: '1em', fontWeight: '600', opacity: isAccountLoading ? 0.6 : 1,
                }}>
                  {isAccountLoading ? '处理中...' : '永久注销账号'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      
    </div>
  );
}

export default PersonalCenter;